"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

/**
 * ClerkErrorInterceptor - Handles Clerk.js SDK errors gracefully
 * This component specifically targets the "Object Not Found Matching Id" errors
 * that occur when Clerk's internal object registry gets out of sync
 */
export default function ClerkErrorInterceptor() {
  useEffect(() => {
    // Store original console.error
    const originalConsoleError: typeof console.error = console.error;

    // Override console.error to intercept Clerk errors
    console.error = function (...args: unknown[]) {
      const message = args.join(" ");

      // Check for Clerk-specific error patterns
      if (
        message.includes("Object Not Found Matching Id") &&
        message.includes("MethodName:update")
      ) {
        console.warn(
          "🔄 [ClerkErrorInterceptor] Intercepted Clerk SDK error:",
          message
        );

        // Log to Sentry with reduced severity
        Sentry.captureMessage(`Clerk SDK Error Intercepted: ${message}`, {
          level: "warning",
          tags: {
            component: "ClerkErrorInterceptor",
            errorType: "clerk_object_not_found",
            intercepted: true,
          },
          extra: {
            fullMessage: message,
            arguments: args,
            userAgent: navigator?.userAgent,
            url: window?.location?.href,
            timestamp: new Date().toISOString(),
          },
        });

        // Don't call original console.error for this specific error
        return;
      }

      // For all other errors, call the original console.error
      originalConsoleError.apply(console, args);
    };

    // Enhanced Promise rejection handler specifically for Clerk
    const handleClerkPromiseRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const reasonString = String(reason || "");

      // Specifically handle Clerk "Object Not Found" errors
      if (
        reasonString.includes("Object Not Found Matching Id") &&
        reasonString.includes("MethodName:update")
      ) {
        console.warn(
          "🔄 [ClerkErrorInterceptor] Handled Clerk promise rejection:",
          reasonString
        );

        // Extract ID and method info for better debugging
        const idMatch = reasonString.match(/Id:(\d+)/);
        const methodMatch = reasonString.match(/MethodName:(\w+)/);
        const paramMatch = reasonString.match(/ParamCount:(\d+)/);

        Sentry.captureException(
          new Error(`Clerk SDK Promise Rejection: ${reasonString}`),
          {
            level: "warning",
            tags: {
              component: "ClerkErrorInterceptor",
              errorType: "clerk_promise_rejection",
              handled: true,
              objectId: idMatch?.[1] || "unknown",
              method: methodMatch?.[1] || "unknown",
              paramCount: paramMatch?.[1] || "unknown",
            },
            extra: {
              originalReason: reason,
              reasonType: typeof reason,
              extractedId: idMatch?.[1],
              extractedMethod: methodMatch?.[1],
              extractedParamCount: paramMatch?.[1],
            },
          }
        );

        // Prevent the error from propagating and showing in browser console
        event.preventDefault();

        // Optional: Attempt to recover by clearing Clerk's local state
        try {
          // Clear any cached Clerk data that might be causing the mismatch
          if (typeof window !== "undefined" && window.localStorage) {
            Object.keys(localStorage).forEach((key) => {
              if (
                key.startsWith("clerk-session") ||
                key.startsWith("clerk-user")
              ) {
                localStorage.removeItem(key);
              }
            });
          }
        } catch (e) {
          console.warn("Could not clear Clerk localStorage:", e);
        }

        return;
      }

      // For chunk loading errors related to Clerk
      if (
        reasonString.includes("ChunkLoadError") ||
        reasonString.includes("98150de33c239da6.js")
      ) {
        console.warn(
          "🔄 [ClerkErrorInterceptor] Handled Clerk chunk loading error:",
          reasonString
        );

        Sentry.captureException(
          new Error(`Clerk Chunk Load Error: ${reasonString}`),
          {
            level: "error", // This is more serious as it affects functionality
            tags: {
              component: "ClerkErrorInterceptor",
              errorType: "clerk_chunk_load_error",
              handled: true,
            },
          }
        );

        event.preventDefault();

        // For chunk loading errors, we should reload the page after a short delay
        console.log(
          "🔄 [ClerkErrorInterceptor] Scheduling page reload due to chunk loading error..."
        );
        setTimeout(() => {
          window.location.reload();
        }, 2000);

        return;
      }
    };

    // Add the enhanced promise rejection handler
    window.addEventListener(
      "unhandledrejection",
      handleClerkPromiseRejection,
      true
    );

    // Cleanup function
    return () => {
      // Restore original console.error
      if (originalConsoleError) {
        console.error = originalConsoleError;
      }

      // Remove event listener
      window.removeEventListener(
        "unhandledrejection",
        handleClerkPromiseRejection,
        true
      );
    };
  }, []);

  return null; // This component doesn't render anything visible
}
